/**
 * Edit / delete of one's own message in a bridged thread: what the CSP offers
 * and says (cs-analysis docs/design-client-comms.md §5.1 "A delete or an edit
 * during a send" and its "What a client shows" table, CT-15747).
 *
 * A message the bridge sends goes: claimed (`waOutboundAt`) → queued at
 * Periskope (`waUniqueId`) → its WhatsApp copy comes back and links the id
 * (`waMessageId`). A final outcome stays on `waOutboundError`. Until the id is
 * back the api can change the message here only, so it refuses an edit
 * (409 `{ code, message }`) and answers a delete with `whatsapp`: what
 * happened there. Every line below must be true in every case the table
 * lists for its answer.
 */

/**
 * `waOutboundError` markers: it never went to WhatsApp (final): the rail refused the group, its attachment was refused,
 * it was deleted before its send, or the bridge gave up on it (OUTBOUND_GAVE_UP, cs-analysis #398). The api's
 * OUTBOUND_NEVER_SENT (cs-analysis packages/shared/src/comms/bridge-outbound.ts).
 */
export const OUTBOUND_NEVER_SENT = ['blocked_by_allowlist', 'media_too_large_for_bridge', 'media_object_missing', 'deleted_before_send', 'periskope_refused', 'send_timed_out'];
/**
 * `waOutboundError` markers: its WhatsApp copy could not be confirmed, and waiting will not tell (final; an echo clears
 * all but `whatsapp_failed`, a later ack clears that). The api's OUTBOUND_UNCONFIRMED. `send_unanswered` is not one:
 * held for its echo, it reads as on its way.
 */
export const OUTBOUND_UNCONFIRMED = ['orphaned_claim', 'unlinked_after_send', 'unanswered_no_echo', 'whatsapp_failed'];
/** WhatsApp reported the send failed (ack -1): the one unconfirmed marker a linked message can carry. */
const WHATSAPP_FAILED = 'whatsapp_failed';

/** On WhatsApp: the api's own test, a WhatsApp message id. */
export const on_whatsapp = (m) => !!(m && m.waMessageId);

/**
 * Its WhatsApp copy could not be confirmed: the api's whatsAppCopyUnconfirmed. `whatsapp_failed` counts while the
 * bridge's claim is held (`waOutboundAt`), linked or not; the other markers only with no WhatsApp id.
 */
export const copy_unconfirmed = (m) => {
    if (!m) return false;
    if (m.waOutboundError === WHATSAPP_FAILED) return m.waOutboundAt != null;
    return !on_whatsapp(m) && OUTBOUND_UNCONFIRMED.includes(m.waOutboundError);
};

/** Claimed by the bridge, no WhatsApp id yet, nothing saying it never went or its copy is unknown: there or about to be. */
export const on_its_way_to_whatsapp = (m) => !!m && !on_whatsapp(m) && m.waOutboundAt != null
    && !OUTBOUND_NEVER_SENT.includes(m.waOutboundError) && !OUTBOUND_UNCONFIRMED.includes(m.waOutboundError);

/**
 * ✎ on an own message with text that left through Infurnia. A message typed on
 * a phone in the WhatsApp group (origin WHATSAPP) is the representative's own
 * when their number is mapped to them, but WhatsApp lets only that phone edit
 * it. The rep app's rule (cs-analysis packages/comms-client/src/message-actions.ts).
 */
export const edit_offered = (m, is_mine) => !!is_mine && m.origin !== 'WHATSAPP' && (m.body || '').trim() !== '';

/**
 * The question when the message could not be read just now (the rep app's deleteConfirmation(null), cs-analysis
 * packages/comms-client/src/message-actions.ts): true wherever the message is.
 */
export const DELETE_QUESTION_UNCHECKED = 'We could not check this message just now. If it is on WhatsApp, deleting it here may leave it there. Delete it anyway?';

/**
 * The row a delete is asked about: the row as the api has it now, or null when it could not be read. The thread's
 * own copy is not trusted after a failed read: a failed read comes with the stream gaps that leave that copy stale.
 * An internal note keeps it: nothing ever changes a message's visibility, and it never goes to WhatsApp. The rep
 * app's askDelete rule.
 */
export const delete_row = (fresh, held) => fresh || (held && held.visibility === 'INTERNAL' ? held : null);

/** The question before a delete, by where the message is; `m` null = it could not be read just now. */
export const delete_question = (m) => {
    if (m == null) return DELETE_QUESTION_UNCHECKED;
    // Before on WhatsApp: a linked message WhatsApp reported failed is never deleted there first.
    if (copy_unconfirmed(m)) {
        return "We could not confirm this message's copy on WhatsApp, so deleting it here may leave it there. Delete it anyway?";
    }
    // The thread does not know whether its group is still connected; the answer's `whatsapp` says what happened.
    if (on_whatsapp(m)) return 'Delete this message for everyone? While this group is connected to WhatsApp, it is removed there too.';
    if (on_its_way_to_whatsapp(m)) {
        return 'WhatsApp has not confirmed this message yet. If you delete it now, it may stay on WhatsApp. '
            + 'To remove it there too, cancel, wait a moment, then delete it. Delete it now?';
    }
    if (m.visibility === 'INTERNAL') return 'Delete this internal note? Only representatives could see it.';
    return 'Delete this message for everyone?';
};

/**
 * What a delete did on WhatsApp (the answer's `whatsapp`), as one line; `alert` when the representative may have to
 * act. `before` is the row the question was asked about, null when it could not be read (that question allowed WhatsApp).
 */
export const delete_outcome = (whatsapp, before) => {
    switch (whatsapp) {
        case 'deleted':
            return { text: 'Deleted here and on WhatsApp.' };
        case 'owed':
            return {
                text: 'Deleted here. It is not removed from WhatsApp yet: it should be removed there once WhatsApp confirms it. '
                    + 'If it is still there later, delete it in WhatsApp itself.',
                alert: true,
            };
        case 'unconfirmed':
            return { text: 'Deleted here, but it may still be on WhatsApp. If it is there, delete it in WhatsApp itself.', alert: true };
        case 'here-only':
            // Worth a line only when the question said it might be on WhatsApp.
            return before == null || on_whatsapp(before) || on_its_way_to_whatsapp(before) || copy_unconfirmed(before)
                ? { text: 'Deleted. It never reached WhatsApp, so there was nothing to remove there.' }
                : null;
        default:
            // An api without `whatsapp` (before #262): the "(deleted)" line says it, as before. A value this list
            // does not know must not read as "removed" after a question that said it is removed there too.
            return whatsapp == null ? null : { text: DELETE_OUTCOME_OTHER, alert: true };
    }
};

/** A `whatsapp` value this list does not know. */
export const DELETE_OUTCOME_OTHER = 'Deleted here. Whether it was removed from WhatsApp is not known: if it is there, delete it in WhatsApp itself.';

/** Deleted already, by this representative in another tab or in the app: nothing to ask, nothing to send. */
export const ALREADY_DELETED = 'Already deleted.';

/** The row as the api has it says it is deleted. */
export const already_deleted = (m) => !!(m && m.deletedAt);

/** The delete's 404 for a message the api has deleted already (ownMessageOrThrow: "message not found"); any other error is not. */
export const delete_not_found = (err) => !!err && err.status === 404
    && !!err.body && typeof err.body === 'object' && err.body.message === 'message not found';

/** The edit's refusals (409), by the api's `code`: the api's own sentences, verbatim (§5.1), so every client says the same. */
export const EDIT_CONFLICT_TEXT = {
    NOT_SENT_BY_INFURNIA: 'Not edited: this message was sent from a phone on WhatsApp, and only that phone can edit it.',
    MESSAGE_IN_FLIGHT: 'Not edited: WhatsApp has not confirmed this message yet, so the edit could not reach it there. Nothing was changed. Try again in a moment.',
    WHATSAPP_COPY_UNCONFIRMED: "Not edited: we could not confirm this message's copy on WhatsApp, so an edit cannot reach it there. Nothing was changed.",
    WHATSAPP_NOT_CONNECTED: 'Not edited: this message is on WhatsApp, and this group is no longer connected to WhatsApp, so the edit cannot reach it there. Nothing was changed.',
};

/** A 409 with a code this list does not know. */
export const EDIT_CONFLICT_OTHER = 'Not edited: this message cannot be changed right now. Nothing changed.';

/**
 * The WhatsApp call of an edit or a delete failed (400 `{ code, message }`, nothing changed here), per action: the
 * api's own sentences, verbatim (cs-analysis MESSAGE_WHATSAPP_ERROR_TEXT, packages/shared/src/comms/bridge-outbound.ts).
 * WHATSAPP_REFUSED: WhatsApp did not make the change. WHATSAPP_NO_ANSWER: no answer came, it may have.
 */
export const WHATSAPP_ERROR_TEXT = {
    edit: {
        WHATSAPP_REFUSED: 'Not edited: WhatsApp did not accept the edit. Nothing was changed here or on WhatsApp.',
        WHATSAPP_NO_ANSWER: 'Not edited here: WhatsApp did not answer, so the edit may or may not have reached the message there. Nothing was changed here.',
    },
    delete: {
        WHATSAPP_REFUSED: 'Not deleted: WhatsApp did not accept the delete. Nothing was changed here or on WhatsApp.',
        WHATSAPP_NO_ANSWER: 'Not deleted here: WhatsApp did not answer, so the message may or may not have been removed there. Nothing was changed here.',
    },
};

/** The api's `code` of a refusal (cs_analysis_fetch's error), or null. */
const api_code = (err) => (err.body && typeof err.body === 'object' && typeof err.body.code === 'string' ? err.body.code : null);
const has = (o, k) => Object.prototype.hasOwnProperty.call(o, k);

/**
 * The sentence for a coded 400 of an edit / a delete (`action`), or null: a 400 with no code (an api before it, one
 * uncoded 400 for every failed WhatsApp call) or a code not in the list, and any other error, are shown as before.
 */
export const whatsapp_refusal = (action, err) => {
    if (!err || err.status !== 400) return null;
    const code = api_code(err);
    return code && has(WHATSAPP_ERROR_TEXT[action], code) ? WHATSAPP_ERROR_TEXT[action][code] : null;
};

/** The line for a refused edit (409, or a coded 400), or null for any other error (shown as before). `err` is cs_analysis_fetch's. */
export const edit_refusal = (err) => {
    if (!err) return null;
    if (err.status === 400) return whatsapp_refusal('edit', err);
    if (err.status !== 409) return null;
    const code = api_code(err);
    return (code && has(EDIT_CONFLICT_TEXT, code) && EDIT_CONFLICT_TEXT[code]) || EDIT_CONFLICT_OTHER;
};
